/**
 * Promo code localStorage management with expiration
 */

const PROMO_CODE_KEY = 'alli_promo_code';
const PROMO_EXPIRATION_HOURS = 24; // 1 day expiration

interface StoredPromoCode {
  code: string;
  expiresAt: number;
  createdAt: number;
}

/**
 * Save promo code to localStorage with expiration
 */
export function savePromoCode(code: string): void {
  try {
    const now = Date.now();
    const data: StoredPromoCode = {
      code,
      expiresAt: now + (PROMO_EXPIRATION_HOURS * 60 * 60 * 1000),
      createdAt: now
    };
    localStorage.setItem(PROMO_CODE_KEY, JSON.stringify(data));
    console.log(`[PromoStorage] Saved promo code: ${code}, expires in ${PROMO_EXPIRATION_HOURS} hours`);
  } catch (error) {
    console.error('[PromoStorage] Error saving promo code:', error);
  }
}

/**
 * Get promo code from localStorage, checking expiration
 * Returns null if expired or not found
 */
export function getPromoCode(): string | null {
  try {
    const stored = localStorage.getItem(PROMO_CODE_KEY);
    if (!stored) {
      return null;
    }

    // Try parsing as new format (with expiration)
    try {
      const data: StoredPromoCode = JSON.parse(stored);

      // Check if expired
      if (Date.now() > data.expiresAt) {
        console.log('[PromoStorage] Promo code expired, removing');
        removePromoCode();
        return null;
      }

      console.log('[PromoStorage] Found valid promo code:', data.code);
      return data.code;
    } catch (parseError) {
      // Legacy format - just a string, treat as expired and remove
      console.log('[PromoStorage] Found legacy promo code format, removing (treating as expired)');
      removePromoCode();
      return null;
    }
  } catch (error) {
    console.error('[PromoStorage] Error getting promo code:', error);
    return null;
  }
}

/**
 * Remove promo code from localStorage
 */
export function removePromoCode(): void {
  try {
    localStorage.removeItem(PROMO_CODE_KEY);
    console.log('[PromoStorage] Removed promo code from localStorage');
  } catch (error) {
    console.error('[PromoStorage] Error removing promo code:', error);
  }
}

/**
 * Check if a promo code exists and is valid
 */
export function hasValidPromoCode(): boolean {
  return getPromoCode() !== null;
}
